import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PersonalService } from '../modules/personal.service';
import { CompaniesService } from '../modules/companies.service';
import { EmployerService } from '../modules/employer.service';
import { WorkShiftRotationService } from '../modules/work-shift-rotation.service';
import { WorkShiftConfiguration } from '../../interfaces/interfaces';
import {
  RotationPattern,
  WeeklyPattern,
  buildRotationPattern,
  buildWeeklyPatternFromWorkShift,
  mapScheduleResponse,
  MappedSchedule
} from '../../utils/work-schedule-calendar.util';
import { AssignRotationBody } from '../../utils/work-shift-rotation.util';

const PATTERN_COLOR_PALETTE = ['blue', 'teal', 'green', 'orange', 'purple', 'indigo'];

/**
 * Único punto con efectos secundarios (HTTP) para el calendario de "Programación de Horarios".
 * La lógica de cálculo pura vive en work-schedule-calendar.util.ts; este servicio solo
 * obtiene/persiste datos y delega el mapeo a esas funciones.
 */
@Injectable({ providedIn: 'root' })
export class WorkScheduleCalendarDataService {

  constructor(
    private personalService: PersonalService,
    private companiesService: CompaniesService,
    private employerService: EmployerService,
    private rotationService: WorkShiftRotationService
  ) { }

  /**
   * Rotaciones activas del proyecto (generales + propias), resueltas contra los turnos ya
   * cargados. Un fallo del catálogo (p. ej. backend sin el endpoint desplegado) no debe tumbar el
   * calendario: simplemente no hay tarjetas de rotación que ofrecer.
   */
  getRotationPatterns(projectId: string, patterns: WeeklyPattern[]): Observable<RotationPattern[]> {
    if (!projectId) { return of([]); }
    return this.rotationService.getByProject(projectId).pipe(
      map(list => list.filter(r => r.isActive).map(r => buildRotationPattern(r, patterns))),
      catchError(() => of([]))
    );
  }

  /** Asigna la rotación al contrato desde `startDate` (`endDate` nulo = indefinida). El backend
   * responde con la programación del mes de `startDate`, que no es necesariamente el visible: el
   * llamador vuelve a pedir el mes en pantalla. */
  assignRotation(personalEmployerId: string | number, body: AssignRotationBody): Observable<any> {
    return this.employerService.putPersonalReportScheduleRotation(personalEmployerId, body);
  }

  /** Termina la rotación vigente del contrato en `from − 1`. */
  endRotation(personalEmployerId: string | number, from: string): Observable<any> {
    return this.employerService.deletePersonalReportScheduleRotation(personalEmployerId, from);
  }

  /**
   * `preloadedWorkShifts`: cards-personal.component.ts ya pide `api/WorkShift/Project/{id}` al
   * seleccionar el contrato (para el selector "Horario de trabajo") y guarda el resultado en
   * `item.workShiftList`. Si lo recibimos, lo reusamos en vez de volver a pedirlo — sólo se
   * llama a la API como respaldo si todavía no se cargó.
   */
  getWeeklyPatterns(projectId: string, preloadedWorkShifts?: any[]): Observable<WeeklyPattern[]> {
    if (preloadedWorkShifts && preloadedWorkShifts.length) {
      return of(this.mapWorkShiftsToPatterns(preloadedWorkShifts));
    }
    if (!projectId) { return of([]); }
    return this.personalService.getPersonalWorkShiftrprojectId(projectId).pipe(
      map((rest: any) => this.mapWorkShiftsToPatterns(rest || [])),
      catchError(() => of([]))
    );
  }

  private mapWorkShiftsToPatterns(workShifts: any[]): WeeklyPattern[] {
    const active: WorkShiftConfiguration[] = workShifts.filter((w: any) => w.IsActive !== false);
    return active.map((w, i) => buildWeeklyPatternFromWorkShift(w, PATTERN_COLOR_PALETTE[i % PATTERN_COLOR_PALETTE.length]));
  }

  getMaxWeeklyHours(adminInfoId: string): Observable<number> {
    if (!adminInfoId) { return of(0); }
    return this.companiesService.getAdminConfiguration(adminInfoId).pipe(
      map((config: any) => {
        const cfg = Array.isArray(config) ? config[0] : config;
        return cfg?.MaxWorkedTimeByWeek ?? 0;
      }),
      catchError(() => of(0))
    );
  }

  getSchedule(personalEmployerId: string | number, from: string, to: string, patterns: WeeklyPattern[]): Observable<MappedSchedule> {
    return this.employerService.getPersonalReportSchedule(personalEmployerId, from, to).pipe(
      map(resp => mapScheduleResponse(resp, patterns))
    );
  }

  saveSchedule(
    personalEmployerId: string | number,
    from: string,
    to: string,
    days: Array<{ date: string; workShiftId: string; source?: string }>,
    patterns: WeeklyPattern[]
  ): Observable<MappedSchedule> {
    return this.employerService.putPersonalReportSchedule(personalEmployerId, { from, to, days }).pipe(
      map(resp => mapScheduleResponse(resp, patterns))
    );
  }

  clearSchedule(personalEmployerId: string | number, from: string, to: string, patterns: WeeklyPattern[]): Observable<MappedSchedule> {
    return this.employerService.deletePersonalReportSchedule(personalEmployerId, from, to).pipe(
      map(resp => mapScheduleResponse(resp, patterns))
    );
  }
}
